import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Payments as PaymentsIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaContabilidad,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';

/** Clave del programa para validar permisos (roles / menú). */
export const PROGRAMA_GESTION_FORMAS_DE_PAGO = 'gestionformasdepago';

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const formularioVacio = {
  id: null,
  documento: '',
  necesita_referencia: 'N',
  necesita_conciliacion: 'N',
  cuenta_contable: '',
};

const etiquetaSiNo = (valor) =>
  String(valor || '').toUpperCase() === 'S' ? 'Sí' : 'No';

const labelOpcionCatalogo = (opt) => {
  if (!opt || opt.cuenta == null) return '';
  const cu = String(opt.cuenta).trim();
  const nom = opt.nombre != null ? String(opt.nombre).trim() : '';
  return nom ? `${cu} — ${nom}` : cu;
};

const etiquetaCuentaCatalogo = (codigo, filasCatalogo) => {
  const c = String(codigo || '').trim();
  if (!c) return '—';
  const row = (filasCatalogo || []).find((x) => String(x.cuenta ?? '').trim() === c);
  const nom = row?.nombre != null ? String(row.nombre).trim() : '';
  return nom ? `${c} — ${nom}` : c;
};

/**
 * GestionFormasDePago: CRUD de documentos / formas de pago.
 * Seguridad y diseño alineados a FacturaPOS (contexto + permisosPorModulo + FinancePro).
 */
const GestionFormasDePago = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles, nombreNegocio } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_GESTION_FORMAS_DE_PAGO),
    [roleDetalles]
  );
  const puedeModificar = useMemo(
    () => puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_GESTION_FORMAS_DE_PAGO),
    [roleDetalles]
  );
  const puedeEliminar = puedeModificar;

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog && claveLog);

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [form, setForm] = useState(formularioVacio);
  const [guardando, setGuardando] = useState(false);
  const [eliminarId, setEliminarId] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [cuentasCatalogo, setCuentasCatalogo] = useState([]);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);

  const credencialesBase = useCallback(
    () => ({
      usuario: usuarioLog || '',
      password: claveLog || '',
      id_empresa: selectedCompany,
    }),
    [usuarioLog, claveLog, selectedCompany]
  );

  const cargarLista = useCallback(async () => {
    if (!puedeLeer || !puedeOperar) {
      setFilas([]);
      return;
    }
    setCargando(true);
    try {
      const res = await fetch(servidor + '/configuracionbackend_listarformasdepago', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credencialesBase()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'Error al cargar formas de pago');
      }
      setFilas(Array.isArray(data.formas_pago) ? data.formas_pago : []);
    } catch (e) {
      console.error(e);
      showError(e.message || 'Error al cargar formas de pago');
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }, [puedeLeer, puedeOperar, servidor, credencialesBase, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperar) cargarLista();
  }, [puedeLeer, puedeOperar, cargarLista]);

  const cargarCuentasCatalogo = useCallback(async () => {
    if (!puedeOperar) {
      setCuentasCatalogo([]);
      return;
    }
    setCargandoCatalogo(true);
    try {
      const res = await fetch(`${servidor}/contabilidadbackend_listarcatalogoactualizable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          busqueda: '',
          orden: 'cuenta',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'No se pudo cargar el catálogo de cuentas');
      }
      setCuentasCatalogo(data.cuentas || []);
    } catch (e) {
      showError(e.message || 'Error al cargar el catálogo');
      setCuentasCatalogo([]);
    } finally {
      setCargandoCatalogo(false);
    }
  }, [puedeOperar, servidor, credencialesBase, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperar) cargarCuentasCatalogo();
  }, [puedeLeer, puedeOperar, cargarCuentasCatalogo]);

  const cuentaCatalogoSeleccionada = useMemo(() => {
    const c = String(form.cuenta_contable || '').trim();
    if (!c) return null;
    return (
      cuentasCatalogo.find((x) => String(x.cuenta ?? '').trim() === c) || { cuenta: c, nombre: '' }
    );
  }, [form.cuenta_contable, cuentasCatalogo]);

  const abrirNuevo = () => {
    if (!puedeModificar) {
      showError('No tiene permisos para crear registros');
      return;
    }
    setForm(formularioVacio);
    setDialogoAbierto(true);
  };

  const abrirEditar = (row) => {
    if (!puedeModificar) {
      showError('No tiene permisos para modificar registros');
      return;
    }
    setForm({
      id: row.id,
      documento: row.documento || '',
      necesita_referencia:
        String(row.necesita_referencia || 'N').toUpperCase() === 'S' ? 'S' : 'N',
      necesita_conciliacion:
        String(row.necesita_conciliacion || 'N').toUpperCase() === 'S' ? 'S' : 'N',
      cuenta_contable: row.cuenta_contable || '',
    });
    setDialogoAbierto(true);
  };

  const cerrarDialogo = () => {
    if (guardando) return;
    setDialogoAbierto(false);
    setForm(formularioVacio);
  };

  const guardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para guardar');
      return;
    }
    const documento = (form.documento || '').trim();
    if (!documento) {
      showError('El documento es obligatorio');
      return;
    }
    setGuardando(true);
    try {
      const res = await fetch(servidor + '/api/configuracionbackend_guardarformadepago', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          id: form.id,
          documento,
          cuenta_contable: (form.cuenta_contable || '').trim() || null,
          necesita_referencia: form.necesita_referencia === 'S' ? 'S' : 'N',
          necesita_conciliacion: form.necesita_conciliacion === 'S' ? 'S' : 'N',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'Error al guardar');
      }
      showSuccess(form.id ? 'Registro actualizado' : 'Registro creado');
      setDialogoAbierto(false);
      setForm(formularioVacio);
      cargarLista();
    } catch (e) {
      showError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = async () => {
    if (!puedeEliminar) {
      showError('No tiene permisos para eliminar');
      return;
    }
    if (eliminarId == null) return;
    setEliminando(true);
    try {
      const res = await fetch(servidor + '/api/configuracionbackend_eliminarformadepago', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          id: eliminarId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.Error || 'Error al eliminar');
      }
      showSuccess('Registro eliminado');
      setEliminarId(null);
      cargarLista();
    } catch (e) {
      showError(e.message || 'Error al eliminar');
    } finally {
      setEliminando(false);
    }
  };

  const botonNavyPrimarioSx = {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 1.4,
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    boxShadow: 'none',
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
  };

  const botonOutlinedSx = {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 1.4,
    borderColor: FP.color.border,
    color: FP.color.textPrimary,
    '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const bodyCellSx = {
    fontSize: '0.86rem',
    color: FP.color.textPrimary,
    borderBottom: `1px solid ${FP.color.border}`,
  };

  const renderAcceso = (mensaje) => (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Gestión de formas de pago"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Documentos y referencia al pagar`
            : 'Documentos y referencia al pagar'
        }
      />
      <Card sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', mt: 4 }}>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <LockOutlinedIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 1 }} />
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
            Acceso denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
            {mensaje}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  if (!puedeLeer) {
    return renderAcceso('No tienes permisos para acceder a la gestión de formas de pago.');
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Gestión de formas de pago"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Documentos y referencia al pagar`
            : 'Documentos y referencia al pagar'
        }
      />

      <Card sx={{ ...cardShellSx, mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.4,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaymentsIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1rem' }}>
                Documentos / formas de pago
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                Un registro por documento: texto libre, si requiere referencia o conciliación al
                pagar, y cuenta contable opcional.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={cargarLista}
                disabled={!puedeOperar || cargando}
                sx={botonOutlinedSx}
              >
                Actualizar
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={abrirNuevo}
                disabled={!puedeOperar || !puedeModificar}
                sx={botonNavyPrimarioSx}
              >
                Nuevo documento
              </Button>
            </Box>
          </Box>

          {!puedeOperar && (
            <Box
              sx={{
                p: 1.4,
                mb: 2,
                borderRadius: 1.4,
                bgcolor: FP.color.infoSoft,
                color: FP.color.info,
                border: `1px solid ${FP.color.info}33`,
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              Seleccione una empresa con base de datos y asegúrese de tener sesión iniciada para
              administrar formas de pago.
            </Box>
          )}

          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              maxHeight: 'calc(100vh - 320px)',
              overflow: 'auto',
            }}
          >
            <Table size="small">
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Documento
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      ¿Necesita referencia?
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      ¿Necesita conciliación?
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Cuenta contable
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 130 }}>
                    <Typography
                      component="span"
                      sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                    >
                      Acciones
                    </Typography>
                  </TableCell>
                </TableRow>
                {cargando && filas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ ...bodyCellSx, py: 4 }}>
                      <CircularProgress size={28} sx={{ color: FP.color.navy }} />
                    </TableCell>
                  </TableRow>
                ) : filas.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ ...bodyCellSx, color: FP.color.textSecondary, py: 4 }}
                    >
                      No hay registros. Cree uno con &quot;Nuevo documento&quot;.
                    </TableCell>
                  </TableRow>
                ) : (
                  filas.map((r) => {
                    const necesita = String(r.necesita_referencia || 'N').toUpperCase() === 'S';
                    const concilia = String(r.necesita_conciliacion || 'N').toUpperCase() === 'S';
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.documento}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Chip
                            size="small"
                            label={etiquetaSiNo(r.necesita_referencia)}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              bgcolor: necesita ? FP.color.accentBlueSoft : FP.color.surfaceAlt,
                              color: necesita ? FP.color.accentBlue : FP.color.textSecondary,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Chip
                            size="small"
                            label={etiquetaSiNo(r.necesita_conciliacion)}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              bgcolor: concilia ? FP.color.accentBlueSoft : FP.color.surfaceAlt,
                              color: concilia ? FP.color.accentBlue : FP.color.textSecondary,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {etiquetaCuentaCatalogo(r.cuenta_contable, cuentasCatalogo)}
                        </TableCell>
                        <TableCell align="right" sx={bodyCellSx}>
                          <Tooltip title={puedeModificar ? 'Editar' : 'Sin permiso para modificar'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => abrirEditar(r)}
                                disabled={!puedeModificar}
                                sx={{
                                  color: FP.color.accentBlue,
                                  '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              puedeEliminar
                                ? 'Eliminar (bloqueado si está en uso en cobros/pagos)'
                                : 'Sin permiso para eliminar'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setEliminarId(r.id)}
                                disabled={!puedeEliminar}
                                sx={{
                                  color: FP.color.danger,
                                  '&:hover': { bgcolor: FP.color.dangerSoft },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={dialogoAbierto}
        onClose={cerrarDialogo}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaymentsIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              {form.id ? 'Editar documento' : 'Nuevo documento'}
            </Typography>
          </Box>
          <IconButton
            onClick={cerrarDialogo}
            size="small"
            disabled={guardando}
            sx={{ color: FP.color.textSecondary }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, md: 3 },
            pb: { xs: 2, md: 3 },
            pt: { xs: '20px !important', md: '24px !important' },
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25,
          }}
        >
          <TextField
            required
            fullWidth
            label="Documento"
            value={form.documento}
            onChange={(e) => setForm((p) => ({ ...p, documento: e.target.value }))}
            placeholder="Ej. Factura, Recibo, Nota de crédito…"
            helperText="Texto libre: identifique el tipo de documento (único por empresa)"
            disabled={!puedeModificar}
            InputLabelProps={{ shrink: true }}
            sx={fpInputSx}
          />
          <FormControl fullWidth size="small" sx={fpInputSx}>
            <InputLabel shrink>¿Necesita referencia?</InputLabel>
            <Select
              notched
              value={form.necesita_referencia}
              label="¿Necesita referencia?"
              onChange={(e) =>
                setForm((p) => ({ ...p, necesita_referencia: e.target.value }))
              }
              disabled={!puedeModificar}
            >
              <MenuItem value="N">No</MenuItem>
              <MenuItem value="S">Sí</MenuItem>
            </Select>
            <Typography
              sx={{
                mt: 0.5,
                color: FP.color.textSecondary,
                fontSize: '0.76rem',
              }}
            >
              Indique si al registrar pagos debe exigirse número de referencia, cheque,
              transferencia, etc.
            </Typography>
          </FormControl>
          <FormControl fullWidth size="small" sx={fpInputSx}>
            <InputLabel shrink>¿Necesita conciliación?</InputLabel>
            <Select
              notched
              value={form.necesita_conciliacion}
              label="¿Necesita conciliación?"
              onChange={(e) =>
                setForm((p) => ({ ...p, necesita_conciliacion: e.target.value }))
              }
              disabled={!puedeModificar}
            >
              <MenuItem value="N">No</MenuItem>
              <MenuItem value="S">Sí</MenuItem>
            </Select>
            <Typography
              sx={{
                mt: 0.5,
                color: FP.color.textSecondary,
                fontSize: '0.76rem',
              }}
            >
              Indique si esta forma de pago debe incluirse en la conciliación bancaria.
            </Typography>
          </FormControl>
          <Autocomplete
            options={cuentasCatalogo}
            loading={cargandoCatalogo}
            value={cuentaCatalogoSeleccionada}
            onChange={(_e, nueva) => {
              setForm((p) => ({
                ...p,
                cuenta_contable:
                  nueva && nueva.cuenta != null ? String(nueva.cuenta).trim() : '',
              }));
            }}
            getOptionLabel={labelOpcionCatalogo}
            isOptionEqualToValue={(a, b) =>
              String(a?.cuenta ?? '').trim() === String(b?.cuenta ?? '').trim()
            }
            disabled={!puedeModificar}
            onOpen={() => {
              if (!cargandoCatalogo && cuentasCatalogo.length === 0 && puedeOperar) {
                cargarCuentasCatalogo();
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Cuenta contable"
                placeholder="Buscar cuenta (opcional)"
                helperText="Opcional. Puede dejarlo vacío y asignarlo más adelante."
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            )}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={cerrarDialogo}
            startIcon={<CloseIcon />}
            disabled={guardando}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardar}
            disabled={guardando || !puedeModificar || !puedeOperar}
            sx={botonNavyPrimarioSx}
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={eliminarId != null}
        onClose={() => !eliminando && setEliminarId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            fontWeight: 700,
            color: FP.color.textPrimary,
            fontSize: '0.98rem',
          }}
        >
          Eliminar documento
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.9rem' }}>
            ¿Confirma eliminar este registro? Si el registro tenía una forma de pago antigua usada
            en cobros o pagos, el sistema impedirá borrarlo.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={() => setEliminarId(null)}
            disabled={eliminando}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={
              eliminando ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
            }
            onClick={confirmarEliminar}
            disabled={eliminando || !puedeEliminar}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.4,
              bgcolor: FP.color.danger,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#B91C3C', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
            }}
          >
            {eliminando ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionFormasDePago;
